import React, { useEffect, useState } from 'react';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Panel, PanelHead, TaskRow } from '../../components/ui';
import { Goal, SubGoal, Todo } from '../../services/goalService';
import { useTranslation } from '../../i18n';
import { parseBackendDateToLocal } from '../../utils/dateTime';
import { formatDate, formatDurationShort } from '../../utils/format';
import { elapsedSecondsSince } from '../../utils/timer';
import { dayKey, todayKey } from '../../utils/zonedTime';
import { RunningTime } from './GoalStage';
import { moveItem } from './boardData';

/** The sections of a goal (T-190; the app's m-hedef tabs). */
export type GoalTab = 'tasks' | 'notes' | 'time' | 'history';
export const GOAL_TABS: GoalTab[] = ['tasks', 'notes', 'time', 'history'];

const isRunning = (td: Todo) => Boolean(td.isStarted && td.startedAt && !td.completed);

/** Notlar: the goal's note (its description), saved when the field loses focus. */
export const NotesTab: React.FC<{ goal: Goal; onSave: (text: string) => void }> = ({ goal, onSave }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(goal.description ?? '');
  useEffect(() => setText(goal.description ?? ''), [goal.description]);
  const save = () => {
    if (text.trim() !== (goal.description ?? '').trim()) onSave(text.trim());
  };
  return (
    <div className="gb-notes">
      <label htmlFor="gb-notes-text" className="gb-notes__label">{t('hedefler.notesLabel')}</label>
      <textarea
        id="gb-notes-text"
        rows={8}
        value={text}
        placeholder={t('hedefler.notesHint')}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
      />
      <p className="gb-notes__help">{t('hedefler.notesHelp')}</p>
    </div>
  );
};

/** Zaman: the time spent on the goal and on each task, the running one first. */
export const TimeTab: React.FC<{ goal: Goal; onOpen: (stage: SubGoal, todo: Todo) => void }> = ({ goal, onOpen }) => {
  const { t, language } = useTranslation();
  const entries = (goal.subGoals ?? [])
    .flatMap((stage) => (stage.todos ?? []).map((todo) => ({ stage, todo })))
    .filter(({ todo }) => (todo.totalTimeSpent ?? 0) > 0 || isRunning(todo))
    .sort((a, b) => {
      if (isRunning(a.todo) !== isRunning(b.todo)) return isRunning(a.todo) ? -1 : 1;
      return (b.todo.totalTimeSpent ?? 0) - (a.todo.totalTimeSpent ?? 0);
    });
  const total = entries.reduce(
    (sum, { todo }) => sum + (todo.totalTimeSpent ?? 0) + (isRunning(todo) ? elapsedSecondsSince(todo.startedAt as string) : 0),
    0,
  );
  return (
    <Panel as="section" aria-labelledby="gb-time-title">
      <PanelHead id="gb-time-title" title={t('hedefler.timeTotal')} trailing={<strong>{formatDurationShort(total, language) || '0'}</strong>} />
      {entries.length === 0 && <p className="gb-tab__empty">{t('hedefler.timeEmpty')}</p>}
      {entries.map(({ stage, todo }) => {
        const running = isRunning(todo);
        const last = todo.stoppedAt ?? todo.completedAt;
        const lastDate = last ? parseBackendDateToLocal(last) : null;
        return (
          <TaskRow
            key={todo.id}
            title={<bdi>{todo.title}</bdi>}
            running={running}
            leading={<span className={`gb-dot${running ? ' is-live' : ''}`} aria-hidden="true" />}
            meta={running ? undefined : [lastDate ? formatDate(lastDate, language, { day: 'numeric', month: 'short' }) : null, formatDurationShort(todo.totalTimeSpent ?? 0, language)].filter(Boolean).join(' · ')}
            trailing={running ? (
              <span className="gb-tab__live">
                {t('hedefler.timeNow')} · <RunningTime startedAt={todo.startedAt as string} base={todo.totalTimeSpent ?? 0} />
              </span>
            ) : undefined}
            onClick={() => onOpen(stage, todo)}
          />
        );
      })}
    </Panel>
  );
};

interface GoalEvent {
  at: Date;
  text: string;
}

/** What happened in a goal, newest first, from the tree (as the app's goalEvents). */
export const goalEvents = (goal: Goal, t: (key: string, o?: Record<string, string | number>) => string, limit = 30): GoalEvent[] => {
  const when = (raw?: string | null) => (raw ? parseBackendDateToLocal(raw) : null);
  const events: GoalEvent[] = [];
  const created = when(goal.createdAt);
  if (created) events.push({ at: created, text: t('hedefler.eventCreated') });
  for (const s of goal.subGoals ?? []) {
    const added = when(s.createdAt);
    if (added && (!created || Math.abs(added.getTime() - created.getTime()) > 60_000)) {
      events.push({ at: added, text: t('hedefler.eventStage', { title: s.title }) });
    }
    for (const td of s.todos ?? []) {
      const done = when(td.completedAt);
      if (td.completed && done) events.push({ at: done, text: t('hedefler.eventDone', { title: td.title }) });
      const started = when(td.startedAt);
      if (isRunning(td) && started) events.push({ at: started, text: t('hedefler.eventTimer', { title: td.title }) });
    }
  }
  return events.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, limit);
};

/** Geçmiş: the recent events with their day. */
export const HistoryTab: React.FC<{ goal: Goal }> = ({ goal }) => {
  const { t, language } = useTranslation();
  const events = goalEvents(goal, t);
  const today = todayKey();
  return (
    <Panel as="section" aria-labelledby="gb-history-title">
      <PanelHead id="gb-history-title" title={t('hedefler.historyTitle')} />
      {events.length === 0 && <p className="gb-tab__empty">{t('hedefler.historyEmpty')}</p>}
      {events.length > 0 && (
        <ul className="gb-history">
          {events.map((e, i) => (
            <li key={`${e.at.getTime()}-${i}`}>
              <span className="gb-history__day">
                {dayKey(e.at) === today ? t('common.today') : formatDate(e.at, language, { day: 'numeric', month: 'short' })}
              </span>
              <bdi>{e.text}</bdi>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

/** "Aşamaları sırala": the stages with up and down; Kaydet sends the order (the app's StageOrderSheet). */
export const StageOrderDialog: React.FC<{
  isOpen: boolean;
  stages: SubGoal[];
  onClose: () => void;
  onSave: (ids: string[]) => void;
}> = ({ isOpen, stages, onClose, onSave }) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState(stages);
  useEffect(() => {
    if (isOpen) setOrder(stages);
  }, [isOpen, stages]);
  const move = (at: number, delta: -1 | 1) => setOrder((o) => moveItem(o, at, at + delta));
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('hedefler.reorderStages')}
      size="sm"
      footer={(
        <>
          <button type="button" className="ui-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="ui-btn ui-btn--primary" onClick={() => onSave(order.map((s) => s.id))}>{t('common.save')}</button>
        </>
      )}
    >
      <ol className="gb-order">
        {order.map((s, i) => (
          <li key={s.id}>
            <span className="gb-order__num">{i + 1}</span>
            <bdi className="gb-order__title">{s.title}</bdi>
            <button type="button" className="gb-cellbtn" disabled={i === 0} aria-label={`${t('hedefler.stageUp')}: ${s.title}`} onClick={() => move(i, -1)}>
              <Icon name="chevronUp" aria-hidden="true" />
            </button>
            <button type="button" className="gb-cellbtn" disabled={i === order.length - 1} aria-label={`${t('hedefler.stageDown')}: ${s.title}`} onClick={() => move(i, 1)}>
              <Icon name="chevronDown" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
    </Modal>
  );
};
